<script setup lang="ts">
import { computed } from 'vue';

import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';
import DatePicker from '@satellite/components/elements/DatePicker.vue';

/**
 * Date field type - date picker input
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string];
  /** @deprecated Use update:model-value instead */
  input: [value: string];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

const modelValueString = computed(() =>
  typeof props.modelValue === 'string' ? props.modelValue : ''
);

function handleInput(value: string) {
  emit('update:model-value', value);
  emit('input', value);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <date-picker
      no-margins
      field-density="comfortable"
      :custom-validation-rules="rules"
      :display-field-icon="false"
      :display-field-label="false"
      :model-value="modelValueString"
      :placeholder="field.placeholder"
      :readonly="normalizedProps.readOnly"
      :required="normalizedProps.required"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
